import type { SEOContentBlockProps } from "@/components/seo/SEOContentBlock";

export const seoCopy: Record<string, SEOContentBlockProps> = {
  index: {
    eyebrow: "Family Getaways on the Guadalupe River",
    heading: "Your Riverside Basecamp for Family Getaways Near New Braunfels",
    paragraphs: [
      "Son's Guadalupe is the easiest way to plan family getaways near New Braunfels, TX. Our riverside properties are built around one idea — give families a private, stress-free home base for tubing the Guadalupe River, kayaking, and soaking up the Texas Hill Country. From shaded cabana rentals on the Guadalupe River to riverside glamping tents and tent camping sites, every stay puts you steps from the water.",
      "Whether you're searching for fun things to do with kids near New Braunfels or planning a multi-generational weekend, our properties along River Road give you direct river access, free parking, picnic tables, and on-site staff. Skip the crowded outfitters — book a cabana, grab a tube, and float the Guadalupe right from your own private spot.",
    ],
    links: [
      { label: "Cabana Rentals", to: "/cabanas" },
      { label: "Tubing the Guadalupe", to: "/tubing" },
      { label: "Glamping & Camping", to: "/glamping" },
      { label: "Why Son's", to: "/why-sons" },
    ],
  },
  cabanas: {
    eyebrow: "Cabana Rentals on the Guadalupe River",
    heading: "Private Cabana Rentals Near New Braunfels, TX",
    paragraphs: [
      "Our cabana rentals near New Braunfels are the most relaxing way to spend a day on the Guadalupe River. Each shaded cabana includes a picnic table, direct river access, one free parking spot, and room for up to six guests — your private home base for tubing, kayaking, swimming, and soaking up the Texas sun. No crowded outfitter parking lots, no long shuttle lines.",
      "Cabana rentals on the Guadalupe River are the centerpiece of family getaways near New Braunfels. Pair your cabana with tube rentals, a kayak, or an overnight glamping stay and turn a day trip into a weekend to remember. Located right on River Road, we're minutes from Gruene, Schlitterbahn, and downtown New Braunfels.",
    ],
    links: [
      { label: "Tubing the Guadalupe River", to: "/tubing" },
      { label: "Glamping & Tent Camping", to: "/glamping" },
      { label: "Property Map", to: "/property-map" },
      { label: "Book Now", to: "/book-now" },
    ],
  },
  tubing: {
    eyebrow: "Tubing the Guadalupe River",
    heading: "Tubing Near New Braunfels — The Easiest Float on the Guadalupe",
    paragraphs: [
      "Tubing the Guadalupe River is the #1 thing to do near New Braunfels, and Son's Guadalupe makes it effortless. Reserve a tube online, post up at your private riverside cabana, and float straight from your spot. Our family-friendly stretches mix calm pools with fun, gentle rapids — perfect for first-timers, families with kids, and seasoned floaters alike.",
      "Looking for fun things to do with kids near New Braunfels? Tubing the Guadalupe River with Son's gives you all-day access to clear, spring-fed water, shaded picnic tables, on-site restrooms, and friendly staff. Add kayaks for the adventurous, or pair tubing with a glamping or tent camping stay to turn it into a full weekend getaway.",
    ],
    links: [
      { label: "Cabana Rentals", to: "/cabanas" },
      { label: "Glamping & Camping", to: "/glamping" },
      { label: "Property Map", to: "/property-map" },
      { label: "Tubing FAQs", to: "/faqs" },
    ],
  },
  glamping: {
    eyebrow: "Glamping & Camping on River Road",
    heading: "Glamping Near New Braunfels & Tent Camping on River Road",
    paragraphs: [
      "Sleep where you float. Our glamping near New Braunfels puts you in fully outfitted bell tents on the Guadalupe River — real beds, AC, and the sound of the river right outside your door. Prefer to bring your own gear? We also offer tent camping near New Braunfels with direct river access on classic River Road, the most iconic stretch of the Guadalupe.",
      "Camping on River Road has been a Texas tradition for generations, and Son's Guadalupe keeps it simple — riverside sites, fire pits, restrooms, and a stocked basecamp for tubing the Guadalupe River the next morning. Whether you're planning family getaways near New Braunfels or a weekend with friends, our overnight stays make it easy to wake up on the water.",
    ],
    links: [
      { label: "Cabana Rentals", to: "/cabanas" },
      { label: "Tubing the Guadalupe", to: "/tubing" },
      { label: "Property Map", to: "/property-map" },
      { label: "Book Overnight", to: "/book-now" },
    ],
  },
  whySons: {
    eyebrow: "Why Son's Guadalupe",
    heading: "The Smarter Way to Plan Family Getaways Near New Braunfels",
    paragraphs: [
      "Son's Guadalupe was built for families who want the magic of the Guadalupe River without the chaos of crowded outfitters. From cabana rentals on the Guadalupe River to glamping near New Braunfels and tent camping on River Road, every stay gives you a private basecamp, direct river access, and a calm, family-first vibe.",
      "We own and operate multiple riverside properties between New Braunfels and Seguin, so we can match you with the right experience — quiet floats for first-time tubers, shaded cabanas for big family days, or full overnight stays under the stars. It's the easiest way to plan a Guadalupe River getaway your family will talk about for years.",
    ],
    links: [
      { label: "Cabana Rentals", to: "/cabanas" },
      { label: "Tubing the Guadalupe", to: "/tubing" },
      { label: "Glamping & Camping", to: "/glamping" },
      { label: "See All Properties", to: "/property-map" },
    ],
  },
  propertyMap: {
    eyebrow: "Cabanas, Tubing & Camping on the Guadalupe",
    heading: "Cabana Rentals on the Guadalupe River — All Our Properties",
    paragraphs: [
      "Son's Guadalupe operates multiple properties along the Guadalupe River and River Road, each with its own personality. Some are tailor-made for cabana rentals on the Guadalupe River and big family tubing days, while others are best for glamping near New Braunfels or tent camping on River Road. Use the property map to find the spot that fits your group.",
      "Every property includes direct river access for tubing the Guadalupe River, free parking with your cabana, and a calm, family-friendly atmosphere. Whether you're looking for fun things to do with kids near New Braunfels for the day or a multi-night family getaway, there's a Son's location for it.",
    ],
    links: [
      { label: "Cabanas", to: "/cabanas" },
      { label: "Tubing", to: "/tubing" },
      { label: "Glamping", to: "/glamping" },
      { label: "Book a Property", to: "/book-now" },
    ],
  },
  faqs: {
    eyebrow: "Planning Your Visit",
    heading: "Tubing the Guadalupe River — Everything You Need to Know",
    paragraphs: [
      "Most of our guests are first-timers planning a day of tubing the Guadalupe River, a cabana rental near New Braunfels, or their first glamping near New Braunfels stay. The questions above cover what to bring, parking, river conditions, ages, group sizes, and more. If you don't see your question, our team is one phone call away.",
      "From cabana rentals on the Guadalupe River to tent camping near New Braunfels, our goal is to make every visit simple and stress-free. Family getaways near New Braunfels should be about the river — not logistics — so we've worked hard to remove the friction.",
    ],
    links: [
      { label: "Contact Us", to: "/contact" },
      { label: "Cabanas", to: "/cabanas" },
      { label: "Tubing", to: "/tubing" },
      { label: "Glamping", to: "/glamping" },
    ],
  },
  contact: {
    eyebrow: "We're Here to Help",
    heading: "Plan Your Family Getaway Near New Braunfels",
    paragraphs: [
      "Whether you're booking cabana rentals near New Braunfels, planning a tubing the Guadalupe River day, or putting together a glamping or tent camping weekend, our team can help you pick the right property and the right dates. We answer the phones, we know the river, and we live for family getaways near New Braunfels.",
      "Call us, email us, or send a quick message. We'll help you find the perfect cabana on the Guadalupe River, the right tubing package, or the best riverside campsite on River Road for your group.",
    ],
    links: [
      { label: "Cabanas", to: "/cabanas" },
      { label: "Tubing", to: "/tubing" },
      { label: "Glamping", to: "/glamping" },
      { label: "FAQs", to: "/faqs" },
    ],
  },
  bookNow: {
    eyebrow: "Book Your Riverside Stay",
    heading: "Book Cabanas, Tubing, Glamping & Camping on the Guadalupe River",
    paragraphs: [
      "Book directly with Son's Guadalupe for the best rates on cabana rentals near New Braunfels, tubing the Guadalupe River, glamping near New Braunfels, and tent camping on River Road. One simple booking flow covers every property — pick your dates, your group size, and your experience.",
      "From quick day trips to multi-night family getaways near New Braunfels, our booking team makes it easy. If you're not sure which property is right for your group, give us a call and we'll match you to the right cabana, glamping tent, or campsite on the Guadalupe.",
    ],
    links: [
      { label: "Cabanas", to: "/cabanas" },
      { label: "Tubing", to: "/tubing" },
      { label: "Glamping & Camping", to: "/glamping" },
      { label: "Property Map", to: "/property-map" },
    ],
  },
  specials: {
    eyebrow: "Deals & Promotions",
    heading: "Specials on Family Getaways Near New Braunfels",
    paragraphs: [
      "Save on cabana rentals near New Braunfels, tubing the Guadalupe River, and glamping near New Braunfels with our latest specials. We run seasonal deals on weekday floats, multi-night stays, and big-group bookings — perfect for stretching the family budget on your next Guadalupe River getaway.",
      "Family getaways near New Braunfels don't have to be expensive. Mix and match cabanas, tubing, and overnight glamping or tent camping on River Road to build the perfect weekend — at a price that works.",
    ],
    links: [
      { label: "Book Now", to: "/book-now" },
      { label: "Cabanas", to: "/cabanas" },
      { label: "Tubing", to: "/tubing" },
      { label: "Glamping", to: "/glamping" },
    ],
  },
  blog: {
    eyebrow: "Stories From the River",
    heading: "Guides for Tubing the Guadalupe River & Family Getaways",
    paragraphs: [
      "From insider tips on tubing the Guadalupe River to guides on cabana rentals near New Braunfels, glamping near New Braunfels, and fun things to do with kids near New Braunfels, our blog is the easiest way to plan your next visit. New posts every season covering river conditions, family-friendly itineraries, and the best of New Braunfels and the Texas Hill Country.",
    ],
    links: [
      { label: "Cabanas", to: "/cabanas" },
      { label: "Tubing", to: "/tubing" },
      { label: "Glamping", to: "/glamping" },
      { label: "Book Now", to: "/book-now" },
    ],
  },
  // Property booking pages
  bookCabanasGeneric: {
    eyebrow: "Riverside Stay",
    heading: "Cabana Rentals, Tubing & Camping on the Guadalupe River",
    paragraphs: [
      "This Son's property is built for tubing the Guadalupe River, cabana rentals on the Guadalupe River, and family getaways near New Braunfels. Direct river access, free parking with cabana bookings, picnic tables, and a calm, family-friendly basecamp — everything you need for a great day or weekend.",
      "Pair your stay with tube rentals, kayaks, glamping near New Braunfels, or tent camping on River Road to build the perfect Hill Country escape.",
    ],
    links: [
      { label: "All Properties", to: "/property-map" },
      { label: "Cabanas", to: "/cabanas" },
      { label: "Tubing", to: "/tubing" },
      { label: "Glamping", to: "/glamping" },
    ],
  },
};
